import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import {
  uploadChatAttachments,
} from "../../utils/api/webChatApi";
import {
  getChannelChatSessions,
  getLatestChannelChatSession,
  getChannelChatHistory,
  createChannelChatSessionId,
  getChannelChatContextIdentity,
} from "../../utils/api/channelChatApi";
import {
  getChannelChatSocket,
  joinChannelChatSession,
} from "../../utils/chat/channelChatSocket";

import {
  UPDATE_TASK_STATUS,
  UPDATE_ACTION_STATUS,
  RUN_ALL_TASKS_BY_PRODUCT_ID,
  RUN_ALL_TASKS_BY_ARTICLE_ID,
} from "../../graphql/mutations";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./ChatPage.module.css";
import { ChatStatusFlow } from "./ChatStatusFlow";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  HelpCircle,
  BarChart2,
  TrendingUp,
  ShoppingCart,
  Package,
  Zap,
  MessageCircle,
  Settings,
  FileText,
  Lightbulb,
  Search,
  Plus,
} from "lucide-react";

const nowIso = () => new Date().toISOString();

const logChannelFlow = (event, payload = {}) => {
  console.log(`[channel-chat][frontend][${nowIso()}] ${event}`, payload);
};

const CHAT_IMAGE_URL_REGEX = /\.(png|jpe?g|webp|gif|avif|svg)(?:[?#]|$)/i;
const isChatImageUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value) && CHAT_IMAGE_URL_REGEX.test(value);
const LABELED_CHAT_IMAGE_BLOCK_REGEX =
  /^(Current hero image|Suggested hero image)\s*:?\s*\n((?:https?:\/\/)\S+\.(?:png|jpe?g|webp|gif|avif|svg)(?:\?\S*)?(?:#\S*)?)\s*$/gim;
const BARE_CHAT_IMAGE_URL_REGEX =
  /(^|[\s(])((?:https?:\/\/)[^\s<>)\]]+\.(?:png|jpe?g|webp|gif|avif|svg)(?:\?[^\s<>)\]]*)?(?:#[^\s<>)\]]*)?)(?=$|[\s).,;!?])/gi;

const renderableChatContent = (content) => {
  if (typeof content !== "string" || !content) return "";
  const normalized = content.replace(
    LABELED_CHAT_IMAGE_BLOCK_REGEX,
    (_match, label, url) => `[${label}](${url})`
  );
  return normalized.replace(BARE_CHAT_IMAGE_URL_REGEX, (match, prefix, url, offset, source) => {
    // Leave markdown link/image destinations like `](https://...)` untouched.
    if (prefix === "(" && offset > 0 && source[offset - 1] === "]") return match;
    return `${prefix}[View image](${url})`;
  });
};

const normalizeFileName = (value = "conversation") => {
  const base = String(value || "conversation")
    .replace(/\.md$/i, "")
    .replace(/[^\w\s.-]+/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
  return `${base || "conversation"}.md`;
};

const isMarkdownAttachment = (attachment = {}) => {
  const type = String(attachment?.type || attachment?.contentType || "").toLowerCase();
  const name = String(attachment?.name || attachment?.fileName || "").toLowerCase();
  return Boolean(
    attachment?.inlineMarkdown ||
    attachment?.markdown ||
    attachment?.content ||
    type.includes("markdown") ||
    name.endsWith(".md")
  );
};

const roleLabel = (role = "") => {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "human" || normalized === "user") return "User";
  if (normalized === "ai" || normalized === "assistant") return "Zeqi";
  return normalized ? normalized : "Message";
};

const buildConversationMarkdown = ({ session = {}, messages = [], title = "Conversation" }) => {
  const lines = [
    `# ${title}`,
    "",
    `- Session ID: ${session?.sessionId || ""}`,
    session?.channelId ? `- Channel: ${session.channelId}` : null,
    session?.currentContext?.label ? `- Context: ${session.currentContext.label}` : null,
    `- Exported: ${new Date().toISOString()}`,
    "",
    "## Transcript",
  ].filter(Boolean);

  messages.forEach((message, index) => {
    const createdAt = message?.createdAt ? ` - ${message.createdAt}` : "";
    lines.push("");
    lines.push(`### ${index + 1}. ${roleLabel(message?.role)}${createdAt}`);
    lines.push("");
    lines.push(String(message?.content || "").trim() || "_No text content._");
    if (Array.isArray(message?.attachments) && message.attachments.length) {
      lines.push("");
      lines.push("Attachments:");
      message.attachments.forEach((attachment) => {
        lines.push(`- ${attachment?.name || "Attachment"}${attachment?.url ? `: ${attachment.url}` : ""}`);
      });
    }
  });

  return lines.join("\n");
};

const isConversationMarkdownMessage = (message = {}) =>
  String(message?.localId || message?.data?.id || "").startsWith("conversation-md:");

const isContextNotificationMessage = (message = {}) => {
  const id = String(message?.localId || message?.data?.id || "");
  return id.startsWith("context-added:") || id.startsWith("context-removed:");
};

const isLocalOnlyMessage = (message = {}) =>
  isConversationMarkdownMessage(message) || isContextNotificationMessage(message);

const mergeWithLocalConversationArtifacts = (serverMessages = [], currentMessages = []) => {
  const localArtifacts = currentMessages.filter(isLocalOnlyMessage);
  if (!localArtifacts.length) return serverMessages;
  const combined = dedupeChatMessages([...serverMessages, ...localArtifacts]);
  return combined.sort((a, b) => {
    const ta = getMessageTimestampMs(a) || Infinity;
    const tb = getMessageTimestampMs(b) || Infinity;
    return ta - tb;
  });
};

const chatMarkdownComponents = {
  a: ({ href, children, ...props }) => {
    const url = typeof href === "string" ? href.trim() : "";
    if (!isChatImageUrl(url)) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    }

    const childText = Array.isArray(children)
      ? children.join("").trim()
      : String(children || "").trim();
    const alt = childText && childText.toLowerCase() !== "view image" ? childText : "Chat image";
    const showCaption = Boolean(childText) && childText.toLowerCase() !== "view image";

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.chatInlineImageLink}
        title={childText || "Open image"}
      >
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className={styles.chatInlineImageThumb}
        />
        {showCaption ? (
          <span className={styles.chatInlineImageCaption}>{childText}</span>
        ) : null}
      </a>
    );
  },
};

// Icon mapping - maps backend icon names to Lucide components
const ICON_MAP = {
  "help-circle": HelpCircle,
  "bar-chart-2": BarChart2,
  "trending-up": TrendingUp,
  "shopping-cart": ShoppingCart,
  package: Package,
  zap: Zap,
  "message-circle": MessageCircle,
  settings: Settings,
  "file-text": FileText,
  lightbulb: Lightbulb,
};

// Theme to CSS class mapping
const THEME_CLASSES = {
  info: styles.suggestionChipInfo,
  primary: styles.suggestionChipPrimary,
  success: styles.suggestionChipSuccess,
  warning: styles.suggestionChipWarning,
  error: styles.suggestionChipError,
};

// Helper function to get icon component from name
const getSuggestionIcon = (iconName) => {
  if (!iconName) return null;
  const IconComponent = ICON_MAP[iconName];
  return IconComponent ? <IconComponent size={14} /> : null;
};

// Helper function to get theme class
const getSuggestionThemeClass = (theme) => {
  if (!theme) return "";
  return THEME_CLASSES[theme] || "";
};

const LAST_CHANNEL_SESSION_KEY = (userId, channelId, contextIdentity = "main") =>
  `zeqi:channelChatSession:${userId}:${channelId}:${contextIdentity}`;

const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "application/pdf",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const mergeMessages = (humanMessages = [], aiMessages = []) => {
  const parseTime = (value, fallback) => {
    if (!value) return fallback;
    const date = new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? fallback : time;
  };

  const merged = [];
  let seq = 0;

  for (const msg of humanMessages || []) {
    merged.push({
      type: "human",
      data: msg,
      sortKey: parseTime(msg?.createdAt, Date.now() + seq),
      seq: (seq += 1),
    });
  }

  for (const msg of aiMessages || []) {
    merged.push({
      type: "ai",
      data: msg,
      sortKey: parseTime(msg?.createdAt, Date.now() + seq),
      seq: (seq += 1),
    });
  }

  return merged
    .sort((a, b) => {
      if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
      return a.seq - b.seq;
    })
    .map(({ type, data }) => ({ type, data }));
};


const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSessionTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const mergeChannelSocketHistory = (humanMessages = [], aiMessages = []) =>
  mergeMessages(humanMessages, aiMessages);

const isImageType = (type) => type?.startsWith("image/");

const MESSAGE_DEDUPE_WINDOW_MS = 30000;

const getMessageTimestampMs = (message) => {
  const raw = message?.data?.createdAt || message?.data?.updatedAt || null;
  if (!raw) return 0;
  const value = new Date(raw).getTime();
  return Number.isNaN(value) ? 0 : value;
};

const getMessageContentSignature = (message) =>
  String(message?.data?.content || "").trim();

const getMessageAttachmentsSignature = (message) => {
  const attachments = Array.isArray(message?.data?.attachments)
    ? message.data.attachments
    : [];
  return attachments
    .map((att) => `${att?.url || ""}|${att?.name || ""}|${att?.type || att?.contentType || ""}`)
    .join("::");
};

const isPersistedMessage = (message) =>
  Boolean(message?.data?._id || message?.data?.id) && !message?.localId;

const shouldTreatAsDuplicateMessage = (left, right) => {
  if (!left || !right) return false;
  if (left.type !== right.type) return false;

  const leftContent = getMessageContentSignature(left);
  const rightContent = getMessageContentSignature(right);
  if (!leftContent || leftContent !== rightContent) return false;

  if (left.type === "human") {
    const leftAttachments = getMessageAttachmentsSignature(left);
    const rightAttachments = getMessageAttachmentsSignature(right);
    if (leftAttachments !== rightAttachments) return false;
  }

  const leftTime = getMessageTimestampMs(left);
  const rightTime = getMessageTimestampMs(right);
  if (leftTime && rightTime) {
    return Math.abs(leftTime - rightTime) <= MESSAGE_DEDUPE_WINDOW_MS;
  }

  return true;
};

const choosePreferredMessage = (current, candidate) => {
  if (!current) return candidate;
  if (!candidate) return current;

  const currentPersisted = isPersistedMessage(current);
  const candidatePersisted = isPersistedMessage(candidate);
  if (currentPersisted !== candidatePersisted) {
    return candidatePersisted ? candidate : current;
  }

  const currentLength = getMessageContentSignature(current).length;
  const candidateLength = getMessageContentSignature(candidate).length;
  if (candidateLength !== currentLength) {
    return candidateLength > currentLength ? candidate : current;
  }

  const currentTime = getMessageTimestampMs(current);
  const candidateTime = getMessageTimestampMs(candidate);
  return candidateTime >= currentTime ? candidate : current;
};

const dedupeChatMessages = (items = []) => {
  const next = [];
  for (const item of Array.isArray(items) ? items : []) {
    const duplicateIndex = next.findIndex((existing) =>
      shouldTreatAsDuplicateMessage(existing, item)
    );
    if (duplicateIndex === -1) {
      next.push(item);
      continue;
    }
    next[duplicateIndex] = choosePreferredMessage(next[duplicateIndex], item);
  }
  return next;
};

const getMessageRenderKey = (message, index) =>
  message?.data?._id ||
  message?.data?.id ||
  message?.localId ||
  [
    message?.type || "message",
    getMessageTimestampMs(message) || "no-time",
    getMessageContentSignature(message).slice(0, 80) || "no-content",
    index,
  ].join(":");

const buildJoinKey = ({
  userId,
  channelId,
  sessionId,
  chatContextIdentity,
}) =>
  JSON.stringify({
    userId: userId || null,
    channelId: channelId || null,
    sessionId: sessionId || null,
    chatContextIdentity: chatContextIdentity || "main",
  });

const ChatPage = ({
  embedded = false,
  inline = false,
  variant = "default",
  suggestionsOverride = null,
  inputPlaceholder = null,
  channelId = null,
  chatContext = null,
  chatContextTrail = null,
  onContextResolved = null,
  onSessionContextLoaded = null,
  onSessionIdReady = null,
  leadingAiMessage = null,
  localGuardReply = null,
  viewerTimeWindow = null,
}) => {
  const { user } = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.userId || user?._id || user?.id;
  const authToken = user?.token;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [channelChatStatus, setChannelChatStatus] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [responseSuggestions, setResponseSuggestions] = useState([]);
  const [expandedMarkdownAttachments, setExpandedMarkdownAttachments] = useState({});
  // Track loading state per suggestion button (keyed by suggestion id or text)
  const [loadingSuggestionIds, setLoadingSuggestionIds] = useState(new Set());
  const sessionIdRef = useRef(null);
  const chatContextRef = useRef(chatContext);
  const chatContextTrailRef = useRef(chatContextTrail);
  const viewerTimeWindowRef = useRef(viewerTimeWindow);
  const onContextResolvedRef = useRef(onContextResolved);
  const onSessionContextLoadedRef = useRef(onSessionContextLoaded);
  const onSessionIdReadyRef = useRef(onSessionIdReady);
  const endRef = useRef(null);
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);
  const channelChatSocketRef = useRef(null);
  const channelSocketConnectedOnceRef = useRef(false);
  const joinStateRef = useRef({ joinedKey: null, inFlightKey: null });
  const streamingAiMessageRef = useRef(null);
  const materializedConversationContextKeysRef = useRef(new Set());
  const seedHandledRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  // true only when resuming an existing session that may have prior messages
  const isResumingSessionRef = useRef(false);
  const seedMessages = location?.state?.seedMessages || null;
  const { t } = useLocalization();
  const isFullPage = !embedded && !inline;
  const hasChatContent =
    Boolean(leadingAiMessage) ||
    messages.length > 0 ||
    loading ||
    (booting && isResumingSessionRef.current);
  const [chatSessions, setChatSessions] = useState([]);
  const [chatSearch, setChatSearch] = useState("");
  const effectiveChannelId = channelId || "main";
  const chatContextIdentity = useMemo(
    () => getChannelChatContextIdentity(chatContext),
    [chatContext],
  );

  const chatSessionContextKey = useMemo(() => {
    const items = Array.isArray(chatContext?.items)
      ? chatContext.items
      : chatContext
        ? [chatContext]
        : [];
    return items
      .filter(
        (item) =>
          String(item?.type || "").toLowerCase() === "chat_session" &&
          String(item?.sessionId || "").trim(),
      )
      .map((item) => item.key || `chat-session:${item.sessionId}`)
      .sort()
      .join("|");
  }, [chatContext]);

  // Determine if chat is empty
  const isChatEmpty = messages.length === 0 && !booting && !loading;

  // Determine suggestions variant based on context
  const suggestionsVariant = variant === "inline" ? "inline" : variant;

  const suggestions =
    Array.isArray(suggestionsOverride) && suggestionsOverride.length > 0
      ? suggestionsOverride
      : [];

  const renderMessage = useCallback((message, index) => {
    const isAiMessage = message.type === "ai";
    const isHumanMessage = message.type === "human";
    const timestampLabel = message?.data?.createdAt
      ? formatTimestamp(message.data.createdAt)
      : "";
    const cta = message?.data?.cta || null;
    const renderAttachment = (att, idx) => {
      const attachmentKey = `${getMessageRenderKey(message, index)}:${att.key || att.name || idx}`;
      if (isMarkdownAttachment(att)) {
        const markdown = String(att.markdown || att.content || "");
        const isExpanded = expandedMarkdownAttachments[attachmentKey] !== false;
        return (
          <div key={attachmentKey} className={styles.markdownAttachment}>
            <button
              type="button"
              className={styles.markdownAttachmentHeader}
              onClick={() =>
                setExpandedMarkdownAttachments((current) => ({
                  ...current,
                  [attachmentKey]: !(current[attachmentKey] !== false),
                }))
              }
              aria-expanded={isExpanded}
            >
              <FileText size={15} strokeWidth={2.2} aria-hidden="true" />
              <span className={styles.markdownAttachmentTitle}>
                {att.name || att.fileName || "conversation.md"}
              </span>
              <span className={styles.markdownAttachmentToggle}>
                {isExpanded ? "Collapse" : "Expand"}
              </span>
            </button>
            {isExpanded ? (
              <div className={`${styles.markdownAttachmentBody} ${styles.markdown}`}>
                <ReactMarkdown
                  components={chatMarkdownComponents}
                  remarkPlugins={[remarkGfm]}
                >
                  {markdown}
                </ReactMarkdown>
              </div>
            ) : null}
          </div>
        );
      }

      return (
        <a
          key={att.url || attachmentKey}
          href={att.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.messageAttachment}
        >
          {isImageType(att.type) ? (
            <img src={att.url} alt={att.name || "Attachment"} />
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          )}
          <span>{att.name || "File"}</span>
        </a>
      );
    };

    return (
      <div
        key={getMessageRenderKey(message, index)}
        className={
          message.type === "ai"
            ? `${styles.message} ${styles.ai}`
            : `${styles.message} ${styles.human}`
        }
      >
        {isAiMessage ? (
          <div className={styles.zeqiBadge}>Z</div>
        ) : null}
        <div className={styles.messageContent}>
          <div
            className={`${styles.body} ${styles.markdown} ${
              isHumanMessage ? styles.humanBubble : ""
            }`}
          >
            <ReactMarkdown
              components={chatMarkdownComponents}
              remarkPlugins={[remarkGfm]}
            >
              {renderableChatContent(message?.data?.content)}
            </ReactMarkdown>
            {cta?.label && typeof cta?.onClick === "function" ? (
              <button
                type="button"
                className={styles.chatCtaButton}
                onClick={cta.onClick}
              >
                {cta.label}
              </button>
            ) : null}
            {isHumanMessage && message?.data?.attachments?.length > 0 ? (
              <div className={styles.messageAttachments}>
                {message.data.attachments.map(renderAttachment)}
              </div>
            ) : null}
            {isHumanMessage ? (
              <p className={`${styles.meta} ${styles.humanMetaInline}`}>
                {timestampLabel}
              </p>
            ) : null}
          </div>
          {!isHumanMessage && message?.data?.attachments?.length > 0 && (
            <div
              className={`${styles.messageAttachments} ${styles.aiMessageAttachments}`}
            >
              {message.data.attachments.map(renderAttachment)}
            </div>
          )}
        </div>
      </div>
    );
  }, [expandedMarkdownAttachments]);

  useEffect(() => {
    chatContextRef.current = chatContext;
  }, [chatContext]);

  useEffect(() => {
    chatContextTrailRef.current = chatContextTrail;
  }, [chatContextTrail]);

  useEffect(() => {
    viewerTimeWindowRef.current = viewerTimeWindow;
  }, [viewerTimeWindow]);

  useEffect(() => {
    if (!authToken || !userId || booting) return;
    const currentChatContext = chatContextRef.current;
    const contextItems = Array.isArray(currentChatContext?.items)
      ? currentChatContext.items
      : currentChatContext
        ? [currentChatContext]
        : [];
    const conversationItems = contextItems.filter(
      (item) =>
        String(item?.type || "").toLowerCase() === "chat_session" &&
        String(item?.sessionId || "").trim()
    );
    if (!conversationItems.length) return;

    conversationItems.forEach((item) => {
      const sourceSessionId = String(item.sessionId || "").trim();
      const key = item.key || `chat-session:${sourceSessionId}`;
      const materializedKey = key;
      if (!sourceSessionId || sourceSessionId === sessionIdRef.current) return;
      if (materializedConversationContextKeysRef.current.has(materializedKey)) return;
      materializedConversationContextKeysRef.current.add(materializedKey);

      (async () => {
        try {
          const history = item.markdown
            ? {
                sessionId: sourceSessionId,
                channelId: item.collectionName || null,
                currentContext: item,
                messages: [],
              }
            : await getChannelChatHistory({
                sessionId: sourceSessionId,
                userId,
                authToken,
              });
          const title = item.label || "Conversation";
          const markdown =
            item.markdown ||
            buildConversationMarkdown({
              session: history,
              messages: Array.isArray(history?.messages) ? history.messages : [],
              title,
            });
          const fileName = item.fileName || normalizeFileName(title);
          setMessages((current) => {
            if (current.some((message) => message.localId === `conversation-md:${materializedKey}`)) {
              return current;
            }
            return [
              ...current,
              {
                type: "ai",
                localId: `conversation-md:${materializedKey}`,
                data: {
                  id: `conversation-md:${materializedKey}`,
                  content: `Added **${fileName}** as conversation context.`,
                  createdAt: new Date().toISOString(),
                  attachments: [
                    {
                      key: `conversation-md:${materializedKey}:attachment`,
                      name: fileName,
                      type: "text/markdown",
                      inlineMarkdown: true,
                      content: markdown,
                    },
                  ],
                },
              },
            ];
          });
        } catch (error) {
          materializedConversationContextKeysRef.current.delete(materializedKey);
          console.warn("[channel-chat] failed to materialize conversation context", {
            sessionId: sourceSessionId,
            error: error?.message || String(error),
          });
        }
      })();
    });
  }, [authToken, booting, chatSessionContextKey, sessionId, userId]);

  useEffect(() => {
    const handler = (event) => {
      const { type, label, key } = event?.detail || {};
      if (!label) return;

      if (type === "added") {
        const notifId = `context-added:${key || label}:${Date.now()}`;
        setMessages((prev) => [
          ...prev,
          {
            type: "ai",
            localId: notifId,
            data: {
              id: notifId,
              content: `Context **${label}** added to this conversation.`,
              createdAt: new Date().toISOString(),
            },
          },
        ]);
      } else if (type === "removed") {
        const notifId = `context-removed:${key || label}:${Date.now()}`;
        const contextMdPrefix = `conversation-md:${key}`;
        setMessages((prev) => {
          const filtered = prev.filter(
            (msg) => !String(msg?.localId || msg?.data?.id || "").startsWith(contextMdPrefix)
          );
          if (key) materializedConversationContextKeysRef.current.delete(key);
          return [
            ...filtered,
            {
              type: "ai",
              localId: notifId,
              data: {
                id: notifId,
                content: `Context **${label}** has been removed from this conversation.`,
                createdAt: new Date().toISOString(),
              },
            },
          ];
        });
      }
    };

    window.addEventListener("zeqi:chat-context-notification", handler);
    return () => window.removeEventListener("zeqi:chat-context-notification", handler);
  }, []);

  useEffect(() => {
    onContextResolvedRef.current = onContextResolved;
  }, [onContextResolved]);

  useEffect(() => {
    onSessionContextLoadedRef.current = onSessionContextLoaded;
  }, [onSessionContextLoaded]);

  useEffect(() => {
    onSessionIdReadyRef.current = onSessionIdReady;
  }, [onSessionIdReady]);

  const rememberSession = useCallback(
    (sid) => {
      if (!sid || !userId) return;
      setSessionId(sid);
      sessionIdRef.current = sid;
      try {
        localStorage.setItem(
          LAST_CHANNEL_SESSION_KEY(userId, effectiveChannelId, chatContextIdentity),
          sid,
        );
      } catch {
        // ignore
      }
    },
    [chatContextIdentity, effectiveChannelId, userId],
  );

  const loadChatSessions = useCallback(async () => {
    if (!userId || !authToken || !effectiveChannelId) {
      setChatSessions([]);
      return [];
    }

    try {
      const data = await getChannelChatSessions({
        userId,
        channelId: effectiveChannelId,
        scope: effectiveChannelId === "main" ? "all" : "channel",
        limit: 100,
        authToken,
      });
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      setChatSessions(sessions);
      return sessions;
    } catch (err) {
      console.warn("[channel-chat] full session list load failed", err?.message || err);
      return [];
    }
  }, [authToken, effectiveChannelId, userId]);

  const resetComposerState = useCallback(() => {
    setMessages([]);
    setText("");
    setAttachments([]);
    setSelectedSuggestion(null);
    setResponseSuggestions([]);
    setLoadingSuggestionIds(new Set());
    didInitialScrollRef.current = false;
    isNearBottomRef.current = true;
    materializedConversationContextKeysRef.current = new Set();
  }, []);

  useEffect(() => {
    if (!userId || !effectiveChannelId) return;
    let mounted = true;
    (async () => {
      const saved = (() => {
        try {
          return localStorage.getItem(
            LAST_CHANNEL_SESSION_KEY(userId, effectiveChannelId, chatContextIdentity),
          );
        } catch {
          return null;
        }
      })();

      let resolvedSessionId =
        saved || createChannelChatSessionId(userId, effectiveChannelId);
      let hasExistingSession = !!saved;
      if (authToken) {
        try {
          const latest = await getLatestChannelChatSession({
            userId,
            channelId: effectiveChannelId,
            chatContext: chatContextRef.current,
            authToken,
          });
          const latestSessionId = latest?.session?.sessionId || null;
          if (latestSessionId) {
            setChatSessions(latest?.session ? [latest.session] : []);
            resolvedSessionId = latestSessionId;
            // Only resuming if the session has prior messages
            hasExistingSession = (latest?.session?.messageCount ?? latest?.session?.messages?.length ?? 1) > 0;
            const loadedTrail = Array.isArray(latest?.session?.contextTrail)
              ? latest.session.contextTrail
              : [];
            const loadedDetached = Array.isArray(latest?.session?.detachedContextKeys)
              ? latest.session.detachedContextKeys
              : [];
            if (typeof onSessionContextLoadedRef.current === "function") {
              onSessionContextLoadedRef.current(loadedTrail, loadedDetached);
            }
          }
        } catch (err) {
          console.warn("[channel-chat] sessions load failed", err?.message || err);
        }
      }

      if (!mounted) return;
      isResumingSessionRef.current = hasExistingSession;
      rememberSession(resolvedSessionId);
    })();

    return () => {
      mounted = false;
    };
  }, [userId, authToken, effectiveChannelId, rememberSession, loadChatSessions, chatContextIdentity]);

  useEffect(() => {
    if (!authToken || !userId || !effectiveChannelId) return undefined;
    const refreshEventName = "zeqi:channel-chat-refresh-sessions";
    const refreshSessions = () => {
      loadChatSessions();
    };
    window.addEventListener(refreshEventName, refreshSessions);
    return () =>
      window.removeEventListener(refreshEventName, refreshSessions);
  }, [authToken, effectiveChannelId, userId, loadChatSessions]);

  useEffect(() => {
    if (sessionId) {
      sessionIdRef.current = sessionId;
      joinStateRef.current.joinedKey = null;
      joinStateRef.current.inFlightKey = null;
      window.dispatchEvent(
        new CustomEvent("zeqi:chat-session-changed", {
          detail: { sessionId },
        }),
      );
      if (typeof onSessionIdReadyRef.current === "function") {
        onSessionIdReadyRef.current(sessionId);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (!authToken || !userId || !effectiveChannelId) return undefined;

    const socket = getChannelChatSocket({ token: authToken });
    channelChatSocketRef.current = socket;
    if (socket.connected) {
      channelSocketConnectedOnceRef.current = true;
    }

    const handleHistory = (payload = {}) => {
      if (payload?.sessionId !== sessionIdRef.current) return;
      logChannelFlow("socket.history", {
        sessionId: payload?.sessionId,
        humanCount: Array.isArray(payload?.humanMessages) ? payload.humanMessages.length : null,
        aiCount: Array.isArray(payload?.aiMessages) ? payload.aiMessages.length : null,
      });
      const serverMessages = dedupeChatMessages(
        mergeChannelSocketHistory(
          Array.isArray(payload?.humanMessages) ? payload.humanMessages : [],
          Array.isArray(payload?.aiMessages) ? payload.aiMessages : [],
        ),
      );
      setMessages((current) =>
        mergeWithLocalConversationArtifacts(serverMessages, current)
      );
      streamingAiMessageRef.current = null;
      setChannelChatStatus(null);
      setLoading(false);
      setBooting(false);
    };

    const handleHumanMessage = (payload = {}) => {
      if (payload?.sessionId !== sessionIdRef.current) return;
      const incoming = payload?.message;
      if (!incoming) return;
      logChannelFlow("socket.human_message", {
        sessionId: payload?.sessionId,
        contentLength: String(incoming?.content || "").length,
        attachmentCount: Array.isArray(incoming?.attachments) ? incoming.attachments.length : 0,
      });
      setMessages((prev) => {
        const incomingContent = String(incoming?.content || "").trim();
        const incomingCreatedAt = incoming?.createdAt
          ? new Date(incoming.createdAt).getTime()
          : Date.now();

        const optimisticIndex = prev.findIndex((message) => {
          if (message?.type !== "human" || !message?.localId) return false;
          const existingContent = String(message?.data?.content || "").trim();
          const existingCreatedAt = message?.data?.createdAt
            ? new Date(message.data.createdAt).getTime()
            : 0;
          return (
            existingContent === incomingContent &&
            Math.abs(incomingCreatedAt - existingCreatedAt) < 15000
          );
        });

        if (optimisticIndex >= 0) {
          const next = [...prev];
          next[optimisticIndex] = {
            type: "human",
            data: incoming,
          };
          return dedupeChatMessages(next);
        }

        return dedupeChatMessages([
          ...prev,
          {
            type: "human",
            data: incoming,
          },
        ]);
      });
    };

    const handleToken = (payload = {}) => {
      if (payload?.sessionId !== sessionIdRef.current) return;
      const accumulated = String(payload?.accumulated || "");
      logChannelFlow("socket.token", {
        sessionId: payload?.sessionId,
        chunkLength: String(payload?.chunk || "").length,
        accumulatedLength: accumulated.length,
      });
      setMessages((prev) => {
        const tempId =
          streamingAiMessageRef.current || `streaming-ai-${payload.sessionId}`;
        streamingAiMessageRef.current = tempId;
        const next = [...prev];
        const index = next.findIndex(
          (message) => message.localId === tempId || message?.data?.id === tempId,
        );
        const streamingMessage = {
          type: "ai",
          localId: tempId,
          data: {
            id: tempId,
            content: accumulated,
            createdAt: new Date().toISOString(),
          },
        };
        if (index >= 0) {
          next[index] = streamingMessage;
        } else {
          next.push(streamingMessage);
        }
        return next;
      });
      setChannelChatStatus((prev) =>
        prev?.status === "streaming" ? prev : { status: "streaming" },
      );
    };

    const handleComplete = (payload = {}) => {
      if (payload?.sessionId !== sessionIdRef.current) return;
      const incoming = payload?.message;
      logChannelFlow("socket.complete", {
        sessionId: payload?.sessionId,
        contentLength: String(incoming?.content || "").length,
      });
      setMessages((prev) => {
        const tempId = streamingAiMessageRef.current;
        const next = [...prev];
        if (tempId) {
          const index = next.findIndex((message) => message.localId === tempId);
          if (index >= 0) {
            next[index] = {
              type: "ai",
              data: incoming,
            };
            return dedupeChatMessages(next);
          }
        }
        if (incoming) {
          next.push({
            type: "ai",
            data: incoming,
          });
        }
        return dedupeChatMessages(next);
      });
      streamingAiMessageRef.current = null;
      setChannelChatStatus(null);
      setLoading(false);
      if (Array.isArray(payload?.suggestions) && payload.suggestions.length) {
        setResponseSuggestions(payload.suggestions);
      }
      window.dispatchEvent(
        new CustomEvent("zeqi:channel-chat-refresh-sessions"),
      );
    };

    const handleStatus = (payload = {}) => {
      if (payload?.sessionId && payload.sessionId !== sessionIdRef.current) return;
      if (
        payload?.channelId &&
        String(payload.channelId) !== String(effectiveChannelId)
      ) {
        return;
      }
      logChannelFlow("socket.status", {
        sessionId: payload?.sessionId || sessionIdRef.current,
        status: payload?.status || null,
        toolName: payload?.toolName || null,
      });
      setChannelChatStatus(payload);
    };

    const handleError = (payload = {}) => {
      setChannelChatStatus(null);
      setLoading(false);
      setBooting(false);
      console.error("[channel-chat] socket error", payload?.error || payload);
      logChannelFlow("socket.error", {
        sessionId: payload?.sessionId,
        error: payload?.error || payload,
      });
    };

    const handleRouted = (payload = {}) => {
      if (payload?.sessionId !== sessionIdRef.current) return;
      window.dispatchEvent(new CustomEvent("zeqi:channel-chat-refresh-sessions"));
    };

    const handleConnect = () => {
      const activeSessionId = sessionIdRef.current;
      if (!channelSocketConnectedOnceRef.current) {
        channelSocketConnectedOnceRef.current = true;
        return;
      }
      if (!activeSessionId || !userId || !effectiveChannelId) return;

      const reconnectStartedAt = performance.now();
      logChannelFlow("rejoin.emit", {
        userId,
        channelId: effectiveChannelId,
        sessionId: activeSessionId,
        chatContext: chatContextRef.current,
      });

      joinChannelChatSession({
        token: authToken,
        payload: {
          userId,
          channelId: effectiveChannelId,
          sessionId: activeSessionId,
          includeHistory: false,
          chatContext: chatContextRef.current,
        },
      })
        .then((response) => {
          logChannelFlow("rejoin.ack", {
            requestedSessionId: activeSessionId,
            responseSessionId: response?.sessionId,
            ok: response?.ok,
            durationMs: Math.round(performance.now() - reconnectStartedAt),
            error: response?.error || null,
          });
          if (response?.sessionId && response.sessionId !== sessionIdRef.current) {
            rememberSession(response.sessionId);
          }
          if (typeof onContextResolvedRef.current === "function" && response?.currentContext) {
            onContextResolvedRef.current(response.currentContext);
          }
        })
        .catch((error) => {
          logChannelFlow("rejoin.error", {
            requestedSessionId: activeSessionId,
            durationMs: Math.round(performance.now() - reconnectStartedAt),
            error: error?.message || String(error),
          });
        });
    };

    socket.on("connect", handleConnect);
    socket.on("channel-chat:history", handleHistory);
    socket.on("channel-chat:human-message", handleHumanMessage);
    socket.on("channel-chat:status", handleStatus);
    socket.on("channel-chat:token", handleToken);
    socket.on("channel-chat:complete", handleComplete);
    socket.on("channel-chat:error", handleError);
    socket.on("channel-chat:routed", handleRouted);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("channel-chat:history", handleHistory);
      socket.off("channel-chat:human-message", handleHumanMessage);
      socket.off("channel-chat:status", handleStatus);
      socket.off("channel-chat:token", handleToken);
      socket.off("channel-chat:complete", handleComplete);
      socket.off("channel-chat:error", handleError);
      socket.off("channel-chat:routed", handleRouted);
    };
  }, [authToken, effectiveChannelId, userId, rememberSession]);

  useEffect(() => {
    if (!sessionId || !authToken || !effectiveChannelId) return;
    let mounted = true;
    // Only show "Loading chat history" when resuming a session that has prior messages
    const resuming = isResumingSessionRef.current;
    if (resuming) setBooting(true);
    (async () => {
      try {
        const joinKey = buildJoinKey({
          userId,
          channelId: effectiveChannelId,
          sessionId,
          chatContextIdentity,
        });
        if (joinStateRef.current.inFlightKey === joinKey) {
          return;
        }
        if (joinStateRef.current.joinedKey === joinKey && !resuming) {
          return;
        }
        joinStateRef.current.inFlightKey = joinKey;
        const joinStartedAt = performance.now();
        logChannelFlow("join.emit", {
          userId,
          channelId: effectiveChannelId,
          sessionId,
          chatContext: chatContextRef.current,
        });
        const response = await joinChannelChatSession({
          token: authToken,
          payload: {
            userId,
            channelId: effectiveChannelId,
            sessionId,
            includeHistory: resuming,
            chatContext: chatContextRef.current,
          },
        });
        logChannelFlow("join.ack", {
          requestedSessionId: sessionId,
          responseSessionId: response?.sessionId,
          ok: response?.ok,
          durationMs: Math.round(performance.now() - joinStartedAt),
          error: response?.error || null,
        });
        if (!mounted) {
          joinStateRef.current.inFlightKey = null;
          return;
        }
        if (response?.sessionId && response.sessionId !== sessionIdRef.current) {
          rememberSession(response.sessionId);
        }
        if (typeof onContextResolvedRef.current === "function" && response?.currentContext) {
          onContextResolvedRef.current(response.currentContext);
        }
        if (resuming) {
          const serverMessages = dedupeChatMessages(
            mergeChannelSocketHistory(
              Array.isArray(response?.humanMessages) ? response.humanMessages : [],
              Array.isArray(response?.aiMessages) ? response.aiMessages : [],
            ),
          );
          setMessages((current) =>
            mergeWithLocalConversationArtifacts(serverMessages, current)
          );
          streamingAiMessageRef.current = null;
        }
        joinStateRef.current.joinedKey = joinKey;
        joinStateRef.current.inFlightKey = null;
      } catch (err) {
        joinStateRef.current.inFlightKey = null;
        console.warn("[channel-chat] history load failed", err?.message || err);
      } finally {
        if (mounted) setBooting(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [sessionId, authToken, effectiveChannelId, rememberSession, userId, chatContextIdentity]);

  const handleFileSelect = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      // Validate files
      const validFiles = files.filter((file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          console.warn(`[webchat] Invalid file type: ${file.type}`);
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          console.warn(`[webchat] File too large: ${file.name}`);
          return false;
        }
        return true;
      });

      if (!validFiles.length) {
        alert(
          t("ChatPage.invalidFileMsg", {
            defaultValue: "Please select valid image or PDF files under 10MB.",
          }),
        );
        return;
      }

      // Upload files
      setUploading(true);
      try {
        const uploadedFiles = await uploadChatAttachments({
          files: validFiles,
          authToken,
        });

        const successfulUploads = uploadedFiles.filter(
          (f) => !f.error && f.url,
        );
        setAttachments((prev) => [...prev, ...successfulUploads]);
      } catch (err) {
        console.error("[webchat] upload failed", err);
        alert("Failed to upload files. Please try again.");
      } finally {
        setUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [authToken, t],
  );

  const handleRemoveAttachment = useCallback((index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (initialContent) => {
      const content = initialContent != null ? initialContent : text;
      if (!content.trim() && attachments.length === 0) return;
      if (!authToken || !userId) {
        alert(
          t("ChatPage.signInRequired", {
            defaultValue: "Please sign in to chat with Zeqi.",
          }),
        );
        return;
      }

      const tempMessageId = `local-${Date.now()}`;
      const optimistic = {
        type: "human",
        data: {
          content,
          createdAt: new Date().toISOString(),
          attachments: attachments.map((a) => ({
            url: a.url,
            name: a.name,
            type: a.contentType,
          })),
        },
        localId: tempMessageId,
      };
      setMessages((prev) => dedupeChatMessages([...prev, optimistic]));
      setText("");
      // Clear response suggestions when user sends a new message
      setResponseSuggestions([]);

      // Build message with attachments
      const messagePayload = {
        content,
        attachments: attachments.map((a) => ({
          url: a.url,
          name: a.name,
          type: a.contentType,
          key: a.key,
          bucket: a.bucket,
          size: a.size,
        })),
      };

      // Clear attachments after sending
      setAttachments([]);

      setChannelChatStatus({ status: "context_loading" });
      setLoading(true);
      try {
        const socket = channelChatSocketRef.current || getChannelChatSocket({ token: authToken });
        const activeSessionId = sessionIdRef.current;
        const activeChatContext = chatContextRef.current;
        const sendStartedAt = performance.now();
        logChannelFlow("send.emit", {
          userId,
          channelId: effectiveChannelId,
          sessionId: activeSessionId,
          contentLength: String(content || "").length,
          attachmentCount: messagePayload.attachments.length,
          chatContext: activeChatContext,
        });
        await new Promise((resolve, reject) => {
          socket.emit(
            "channel-chat:send",
            {
              userId,
              channelId: effectiveChannelId,
              sessionId: activeSessionId,
              message: messagePayload,
              chatContext: activeChatContext,
              chatContextTrail: chatContextTrailRef.current || null,
              viewerTimeWindow: viewerTimeWindowRef.current || null,
            },
            (response) => {
              logChannelFlow("send.ack", {
                requestedSessionId: activeSessionId,
                responseSessionId: response?.sessionId,
                ok: response?.ok,
                routed: response?.routed || null,
                durationMs: Math.round(performance.now() - sendStartedAt),
                error: response?.error || null,
              });
              if (!response?.ok) {
                reject(new Error(response?.error || "Failed to send channel chat message"));
                return;
              }
              if (response?.sessionId && response.sessionId !== sessionIdRef.current) {
                rememberSession(response.sessionId);
              }
              if (typeof onContextResolvedRef.current === "function" && response?.currentContext) {
                onContextResolvedRef.current(response.currentContext);
              }
              resolve(response);
            },
          );
        });
      } catch (err) {
        console.error("[channel-chat] send failed", err);
        setChannelChatStatus(null);
        setLoading(false);
      }
    },
    [
      text,
      attachments,
      authToken,
      userId,
      rememberSession,
      t,
      effectiveChannelId,
    ],
  );

  useEffect(() => {
    if (seedHandledRef.current) return;
    if (!Array.isArray(seedMessages) || !seedMessages.length) return;
    if (!authToken || !userId) return;
    const first = seedMessages.find((msg) => msg?.data?.content);
    if (!first) return;
    seedHandledRef.current = true;
    handleSubmit(first.data.content);
  }, [seedMessages, authToken, userId, handleSubmit]);

  // When embedded in the right rail, respond to zeqi:chat-seed events
  const handleSubmitRef = useRef(handleSubmit);
  const chatSeedHandledIdsRef = useRef(new Set());
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  });

  useEffect(() => {
    if (!embedded) return;
    try {
      const raw = window.sessionStorage.getItem("zeqi:pendingChatSeed");
      if (raw) {
        const pending = JSON.parse(raw);
        const isFresh = Date.now() - Number(pending?.createdAt || 0) < 120000;
        window.sessionStorage.removeItem("zeqi:pendingChatSeed");
        if (isFresh && pending?.content) {
          if (pending?.seedId) {
            chatSeedHandledIdsRef.current.add(pending.seedId);
          }
          handleSubmitRef.current(pending.content);
        }
      }
    } catch {
      // ignore unavailable storage or malformed payloads
    }
    const handler = (e) => {
      const content = e?.detail?.content;
      if (!content) return;
      const seedId = e?.detail?.seedId;
      if (seedId) {
        if (chatSeedHandledIdsRef.current.has(seedId)) return;
        chatSeedHandledIdsRef.current.add(seedId);
        try {
          const raw = window.sessionStorage.getItem("zeqi:pendingChatSeed");
          const pending = raw ? JSON.parse(raw) : null;
          if (pending?.seedId === seedId) {
            window.sessionStorage.removeItem("zeqi:pendingChatSeed");
          }
        } catch {
          // ignore unavailable storage or malformed payloads
        }
      }
      handleSubmitRef.current(content);
    };
    window.addEventListener("zeqi:chat-seed", handler);
    return () => window.removeEventListener("zeqi:chat-seed", handler);
  }, [embedded]);

  useEffect(() => {
    const handler = (event) => {
      const nextSessionId = event?.detail?.sessionId;
      if (!nextSessionId) return;
      const isNew = Boolean(event?.detail?.isNew);
      isResumingSessionRef.current = !isNew;
      resetComposerState();
      rememberSession(nextSessionId);
      setBooting(!isNew && Boolean(authToken));
    };

    window.addEventListener("zeqi:open-chat-session", handler);
    return () => window.removeEventListener("zeqi:open-chat-session", handler);
  }, [authToken, rememberSession, resetComposerState]);

  useEffect(() => {
    if (booting) return;
    const shouldStickToBottom =
      !didInitialScrollRef.current || isNearBottomRef.current;
    if (!shouldStickToBottom) return;
    endRef.current?.scrollIntoView({
      behavior: didInitialScrollRef.current ? "smooth" : "auto",
    });
    didInitialScrollRef.current = true;
  }, [messages, booting]);

  const handleThreadScroll = (event) => {
    const target = event.currentTarget;
    const bottomGap =
      target.scrollHeight - (target.scrollTop + target.clientHeight);
    isNearBottomRef.current = bottomGap < 80;
  };

  const handleChatShellWheel = useCallback(
    (event) => {
      if (!embedded || variant !== "rightRail") return;
      const threadEl = threadRef.current;
      if (!threadEl) return;

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest("textarea, input, select")) return;

      const scrollableAncestor = target.closest("[data-chat-scrollable]");
      if (scrollableAncestor === threadEl || scrollableAncestor) return;

      threadEl.scrollTop += event.deltaY;
      event.preventDefault();
    },
    [embedded, variant],
  );

  // Mutations for metadata-driven suggestion actions
  const [updateTaskStatus] = useMutation(UPDATE_TASK_STATUS);
  const [updateActionStatus] = useMutation(UPDATE_ACTION_STATUS);
  const [runAllTasksByProductId] = useMutation(RUN_ALL_TASKS_BY_PRODUCT_ID);
  const [runAllTasksByArticleId] = useMutation(RUN_ALL_TASKS_BY_ARTICLE_ID);

  const handleSuggestionClick = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setText(suggestion);
  };

  const handleNewChat = useCallback(() => {
    if (!userId || !effectiveChannelId) return;
    isResumingSessionRef.current = false;
    resetComposerState();
    rememberSession(
      createChannelChatSessionId(userId, effectiveChannelId),
    );
    setBooting(false);
  }, [effectiveChannelId, rememberSession, resetComposerState, userId]);

  const filteredSessions = useMemo(() => {
    const query = chatSearch.trim().toLowerCase();
    if (!query) return chatSessions;
    return chatSessions.filter((session) => {
      const title = String(session?.title || "").toLowerCase();
      const preview = String(session?.preview || "").toLowerCase();
      return title.includes(query) || preview.includes(query);
    });
  }, [chatSearch, chatSessions]);

  // Handle suggestion button click when metadata is present
  const handleMetadataSuggestionClick = async (suggestion) => {
    const metadata = suggestion?.metadata;
    const suggestionKey = suggestion?.id || suggestion?.text || suggestion;

    // If no metadata, fall through to normal chat submission
    if (!metadata) {
      setSelectedSuggestion(suggestion.text || suggestion);
      setResponseSuggestions([]);
      handleSubmit(suggestion.text || suggestion);
      return;
    }

    if (!authToken || !userId) {
      alert(
        t("ChatPage.signInRequired", {
          defaultValue: "Please sign in to chat with Zeqi.",
        }),
      );
      return;
    }

    // Mark this suggestion as loading
    setLoadingSuggestionIds((prev) => new Set([...prev, suggestionKey]));

    try {
      const authContext = {
        context: {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      };

      if (metadata.taskId) {
        await updateTaskStatus({
          variables: { taskId: metadata.taskId, userId },
          ...authContext,
        });
      } else if (metadata.actionId) {
        await updateActionStatus({
          variables: { actionId: metadata.actionId, userId },
          ...authContext,
        });
      } else if (metadata.productId) {
        await runAllTasksByProductId({
          variables: { userId, productId: metadata.productId },
          ...authContext,
        });
      } else if (metadata.articleId) {
        await runAllTasksByArticleId({
          variables: { articleId: metadata.articleId, userId },
          ...authContext,
        });
      } else {
        // Unknown metadata - fall through to normal chat submission
        setResponseSuggestions([]);
        handleSubmit(suggestion.text || suggestion);
        return;
      }

      // Clear the suggestion after successful action
      setResponseSuggestions((prev) =>
        prev.filter((s) => (s?.id || s?.text || s) !== suggestionKey),
      );
    } catch (err) {
      console.error("[webchat] metadata action failed", err?.message || err);
      alert(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setLoadingSuggestionIds((prev) => {
        const next = new Set(prev);
        next.delete(suggestionKey);
        return next;
      });
    }
  };

  // Render minimal chat interface for inline mode (no wrapper, no header, no background)
  if (inline) {
    return (
      <div
        className={styles.chatShell}
        data-inline-chat="true"
        data-chat-empty={hasChatContent ? "false" : "true"}
        data-chat-locked="false"
        onWheelCapture={handleChatShellWheel}
      >
        {hasChatContent && (
          <div
            className={styles.thread}
            ref={threadRef}
            onScroll={handleThreadScroll}
            data-chat-scrollable="thread"
          >
            {booting && messages.length === 0 && isResumingSessionRef.current ? (
              <p className={styles.meta}>Loading chat history…</p>
            ) : null}
            {leadingAiMessage ? renderMessage(leadingAiMessage, "leading") : null}
            {messages.map((message, index) => renderMessage(message, index))}
            {loading && channelChatStatus?.status !== "streaming" && (
              <div className={`${styles.message} ${styles.ai}`}>
                <div className={styles.zeqiBadge}>Z</div>
                <div className={styles.body}>
                  <ChatStatusFlow statusPayload={channelChatStatus} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}

        {/* Attachment preview */}
        {attachments.length > 0 && (
          <div className={styles.attachmentPreview}>
            {attachments.map((att, index) => (
              <div key={att.url || index} className={styles.attachmentItem}>
                {isImageType(att.contentType) ? (
                  <img src={att.url} alt={att.name || "Preview"} />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                )}
                <span className={styles.attachmentName}>
                  {att.name || "File"}
                </span>
                <button
                  type="button"
                  className={styles.removeAttachment}
                  onClick={() => handleRemoveAttachment(index)}
                  aria-label="Remove attachment"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.inputBar} data-tour-id="chat-input">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className={styles.fileInput}
            accept={ALLOWED_FILE_TYPES.join(",")}
            multiple
            onChange={handleFileSelect}
          />

          {/* Attachment button */}
          <button
            type="button"
            className={styles.attachButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading}
            aria-label="Add attachment"
          >
            {uploading ? (
              <span className={styles.uploadingText}>...</span>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
            )}
          </button>

          <textarea
            className={styles.input}
            placeholder={
              inputPlaceholder ||
              t("ChatPage.typeMessage", {
                defaultValue: "Ask Zeqi anything...???",
              })
            }
            disabled={loading || uploading}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <button
            type="button"
            className={styles.sendButton}
            onClick={() => handleSubmit()}
            disabled={loading || uploading}
            aria-label="Send message"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`${!embedded ? pageStyles.page : ""} ${styles.page}`}
      data-page="chat"
      data-embedded={embedded ? "true" : "false"}
      data-suggestions-variant={suggestionsVariant}
      data-chat-locked="false"
    >
      {!embedded ? (
        <header className={styles.header}>
          <button
            type="button"
            className={styles.logoButton}
            onClick={() => navigate("/")}
            aria-label="Go to home"
          >
            <img
              src="/assets/zeqi_black_clearBG.png"
              alt="Zeqi"
              className={styles.avatar}
            />
          </button>
          <div>
            {/* <p className={styles.title}>ZeQi Chat</p>
            <p className={styles.subtitle}>{welcome}</p> */}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => navigate(-1)}
            aria-label="Close chat"
          >
            &times;
          </button>
        </header>
      ) : null}

      <div className={isFullPage ? styles.chatWorkspace : undefined}>
        {isFullPage ? (
          <aside className={styles.sessionRail}>
            <button
              type="button"
              className={styles.newChatButton}
              onClick={handleNewChat}
            >
              <Plus size={16} />
              <span>
                {t("AppShell.newChat", {
                  defaultValue: "New chat with Zeqi",
                })}
              </span>
            </button>

            <div className={styles.sessionRailSection}>
              <p className={styles.sessionRailLabel}>
                {t("AppShell.chatHistory", {
                  defaultValue: "Chat History",
                })}
              </p>
              <label className={styles.sessionSearch}>
                <Search size={14} />
                <input
                  type="search"
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                  placeholder={t("AppShell.searchConversations", {
                    defaultValue: "Search conversations...",
                  })}
                />
              </label>
            </div>

            <div className={styles.sessionList}>
              {filteredSessions.map((item) => {
                const isActive = item.sessionId === sessionId;
                return (
                  <button
                    key={item.sessionId}
                    type="button"
                    className={`${styles.sessionItem} ${
                      isActive ? styles.sessionItemActive : ""
                    }`}
                    onClick={() => {
                      if (!item.sessionId) return;
                      isResumingSessionRef.current = true;
                      resetComposerState();
                      rememberSession(item.sessionId);
                      setBooting(Boolean(authToken));
                    }}
                  >
                    <span className={styles.sessionItemTitle}>
                      {item.title ||
                        t("AppShell.noPreview", {
                          defaultValue: "No messages yet",
                        })}
                    </span>
                    <span className={styles.sessionItemPreview}>
                      {item.preview ||
                        t("AppShell.noPreview", {
                          defaultValue: "No messages yet",
                        })}
                    </span>
                    <span className={styles.sessionItemMeta}>
                      {formatSessionTimestamp(
                        item.lastMessageAt || item.lastActivityAt || item.startedAt,
                      )}
                    </span>
                  </button>
                );
              })}

              {filteredSessions.length === 0 ? (
                <div className={styles.sessionEmptyState}>
                  {chatSearch
                    ? t("AppShell.noConversationMatches", {
                        defaultValue: "No conversations match your search.",
                      })
                    : t("AppShell.noConversationsYet", {
                        defaultValue: "No conversations yet.",
                      })}
                </div>
              ) : null}
            </div>
          </aside>
        ) : null}

        <div
          className={styles.chatShell}
          data-chat-empty={hasChatContent ? "false" : "true"}
          data-chat-locked="false"
          onWheelCapture={handleChatShellWheel}
        >
          {hasChatContent ? (
            <div
              className={styles.thread}
              ref={threadRef}
              onScroll={handleThreadScroll}
              data-chat-scrollable="thread"
            >
              {booting && messages.length === 0 ? (
                <p className={styles.meta}>Loading chat history…</p>
              ) : null}
              {leadingAiMessage ? renderMessage(leadingAiMessage, "leading") : null}
              {messages.map((message, index) => renderMessage(message, index))}
              {loading && channelChatStatus?.status !== "streaming" && (
                <div className={`${styles.message} ${styles.ai}`}>
                  <div className={styles.zeqiBadge}>Z</div>
                  <div className={styles.body}>
                    <ChatStatusFlow statusPayload={channelChatStatus} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          ) : null}

          {/* Attachment preview */}
          {attachments.length > 0 && (
            <div className={styles.attachmentPreview}>
              {attachments.map((att, index) => (
                <div key={att.url || index} className={styles.attachmentItem}>
                  {isImageType(att.contentType) ? (
                    <img src={att.url} alt={att.name || "Preview"} />
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  )}
                  <span className={styles.attachmentName}>
                    {att.name || "File"}
                  </span>
                  <button
                    type="button"
                    className={styles.removeAttachment}
                    onClick={() => handleRemoveAttachment(index)}
                    aria-label="Remove attachment"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

        {/* Suggestions - only show when no messages - displayed as breadcrumbs above chat bar */}
        {isChatEmpty && suggestions.length > 0 && (
          <div
            className={styles.suggestionsContainer}
            data-suggestions-variant={suggestionsVariant}
          >
            {suggestions.map((suggestion) => {
              const IconComponent = getSuggestionIcon(suggestion.icon);
              const themeClass = getSuggestionThemeClass(suggestion.theme);
              return (
                <button
                  key={suggestion.id || suggestion.text || suggestion}
                  type="button"
                  className={`${styles.suggestionChip} ${themeClass} ${
                    selectedSuggestion === (suggestion.text || suggestion)
                      ? styles.suggestionChipActive
                      : ""
                  }`}
                  onClick={() =>
                    handleSuggestionClick(suggestion.text || suggestion)
                  }
                >
                  {IconComponent}
                  <span className={styles.suggestionChipText}>
                    {suggestion.text || suggestion}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Response suggestions from API - shown after AI response */}
        {responseSuggestions.length > 0 && (
          <div
            className={styles.suggestionsContainer}
            data-suggestions-variant={suggestionsVariant}
          >
            {responseSuggestions.map((suggestion) => {
              const IconComponent = getSuggestionIcon(suggestion.icon);
              const themeClass = getSuggestionThemeClass(suggestion.theme);
              const suggestionKey =
                suggestion?.id || suggestion?.text || suggestion;
              const isLoadingThis = loadingSuggestionIds.has(suggestionKey);
              return (
                <button
                  key={suggestionKey}
                  type="button"
                  className={`${styles.suggestionChip} ${themeClass} ${
                    selectedSuggestion === (suggestion.text || suggestion)
                      ? styles.suggestionChipActive
                      : ""
                  } ${isLoadingThis ? styles.suggestionChipLoading : ""}`}
                  disabled={isLoadingThis}
                  onClick={() => handleMetadataSuggestionClick(suggestion)}
                >
                  {isLoadingThis ? (
                    <span
                      className={styles.suggestionChipSpinner}
                      aria-hidden="true"
                    />
                  ) : (
                    IconComponent
                  )}
                  <span className={styles.suggestionChipText}>
                    {suggestion.text || suggestion}
                  </span>
                </button>
              );
            })}
          </div>
        )}

          <div className={styles.inputBar} data-tour-id="chat-input">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className={styles.fileInput}
              accept={ALLOWED_FILE_TYPES.join(",")}
              multiple
              onChange={handleFileSelect}
            />

            {/* Attachment button */}
            <button
              type="button"
              className={styles.attachButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading}
              aria-label="Add attachment"
            >
              {uploading ? (
                <span className={styles.uploadingText}>...</span>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              )}
            </button>

            <textarea
              className={styles.input}
              placeholder={t("ChatPage.typeMessage", {
                defaultValue: "Ask Zeqi anything...!!",
              })}
              disabled={loading || uploading}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={() => handleSubmit()}
              disabled={loading || uploading}
              aria-label="Send message"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
          </button>
        </div>
      </div>
      </div>
    </section>
  );
};

export default ChatPage;
